import React, { Component } from "react";
import DeviceDetailsComponent from "./DeviceDetails";
import { getAllDeviceDetailsBySerialNo, downloadDevicePasswords, downloadDeviceKeys, getDeviceTPMCertificate, downloadDeviceTPMCertificate } from "../../../services/java/java-services";
import { KeyLabels, PasswordLabels, RolePermission } from "../../../helpers/constants";
import { loggerEventName, loggerEventOutcome, loggerEventTypes } from "../../../helpers/messages";
import { logger } from "../../../services/logger/logger-service";
import { getUserRoleType } from "../../../services/aws/aws-services";

let startDate;
export default class DeviceDetailsContainer extends Component {
	constructor(props) {
		super(props);
		const loggedInUserType = getUserRoleType();
		this.state = {
			loading: false,
			deviceSerialNo: props?.location?.state?.deviceId,
			fetchError: '',
			isDownloadingPassword: false,
			isDownloadingKey: false,
			passwordDownloadError: '',
			keyDownloadError: '',
			disbalePasswordDownloadBtn: false,
			disbaleKeyDownloadBtn: false,
			enableTPMCertificate: RolePermission.deviceTPMCertificate.indexOf(loggedInUserType) > -1,
			deviceTPMCertificate: [],
			tpmCertificateDownloadError: '',
			fetchTPMCertificateError: ''
		};
	}

	componentDidMount() {
		startDate = new Date();
		window.addEventListener('beforeunload', this.applicationLogger);
		this.getDeviceDetails();
		if (this.state.enableTPMCertificate) {
			this.getDeviceTPMCertificateDetails();
		}
	}

	componentWillUnmount() {
		window.removeEventListener('beforeunload', this.applicationLogger);
		this.applicationLogger();
	}

	/**
	 * @description function to handle application logs
	 * @memberof DeviceDetailsContainer
	 */
	applicationLogger = () => {
		// calculate the time since we loaded this page
		const timeSinceLoad = (new Date().getTime() - startDate.getTime()) / 1000;
		const loggerObj = {
			EventOutcome: loggerEventOutcome.success,
			EventType: loggerEventTypes.read,
			EventName: loggerEventName.deviceDetails,
			Content: {
				TimeSpent: timeSinceLoad,
				Data: `Device serial no. ${this.state.deviceSerialNo}`
			}
		}
		logger(loggerObj);
	}

	/**
	 * @description Function to fetch details of a device
	 * @memberof DeviceDetailsContainer
	 */
	getDeviceDetails = () => {
		this.setState({ loading: true });
		getAllDeviceDetailsBySerialNo(this.state.deviceSerialNo, (err, resp) => {
			if (err) {
				this.setState({ loading: false, fetchError: err });
				return;
			}
			const unavailablePasswordCount = resp.data.passwords.filter(x => !x[Object.keys(x)[0]]).length;
			const passwords = resp.data.passwords.map((entry) => {
				const key = Object.keys(entry)[0];
				return {
					key,
					value: entry[key],
					isVisible: false,
					type: "password",
					htmlID: key,
					checked: false,
					label: this.getPasswordLabels(key)
				};
			});

			const unavailableKeyCount = resp.data.keys.filter(x => !x[Object.keys(x)[0]]).length;
			const keys = resp.data.keys.map((entry) => {
				const key = Object.keys(entry)[0];
				return {
					key,
					value: entry[key],
					isVisible: false,
					type: "key",
					htmlID: key,
					checked: false,
					label: this.getKeyLabels(key)
				};
			});

			const deviceInfo = {
				details: resp.data.deviceDetails,
				passwords,
				keys
			};

			this.setState({
				loading: false,
				deviceDetails: deviceInfo,
				fetchError: '',
				disbalePasswordDownloadBtn: unavailablePasswordCount === passwords.length,
				disbaleKeyDownloadBtn: unavailableKeyCount === keys.length,
			});
		});
	};

	/**
	 * @description Function to fetch details of a device TPM Certificate
	 * @memberof DeviceDetailsContainer
	 */
	getDeviceTPMCertificateDetails = () => {
		this.setState({ loading: true });
		getDeviceTPMCertificate(this.state.deviceSerialNo, (err, resp) => {
			if (err) {
				this.setState({ loading: false, fetchTPMCertificateError: err });
				return;
			}

			const tpmCert = [
				{
					key: "tpmCert",
					value: resp.data || '',
					isVisible: false,
					type: "tpm",
					htmlID: "tpmCert",
					checked: false,
					label: "Signed TPM Certificate",
				},
			];
			this.setState({
				loading: false,
				deviceTPMCertificate: tpmCert,
				fetchTPMCertificateError: "",
			});
		});
	};

	/**
	 * @description Function to fetch labels for Passwords
	 * @param password String
	 * @returns String
	 * @memberof DeviceDetailsContainer
	 */
	getPasswordLabels = (password) => {
		const data = PasswordLabels.find((x) => x.key.toLowerCase() === password.toLowerCase());
		return data ? data.label : "";
	};

	/**
	 * @description Function to fetch labels for Keys
	 * @param key String
	 * @returns String
	 * @memberof DeviceDetailsContainer
	 */
	getKeyLabels = (key) => {
		const data = KeyLabels.find((x) => x.key.toLowerCase() === key.toLowerCase());
		return data ? data.label : "";
	};

	/**
	 * @description Function to handle checkbox selection for passwords and keys
	 * @param type String (password/key)
	 * @param htmlID String
	 * @param checked Boolean
	 * @memberof DeviceDetailsContainer
	 */
	handleCheckSelection = (type, htmlID, checked) => {
		let paramKey = type === 'password' ? 'passwords' : 'keys'
		this.setState(prevState => {
			return {
				deviceDetails: {
					...prevState.deviceDetails,
					[paramKey]: prevState.deviceDetails[paramKey].map((item) => {
						if (item.htmlID === htmlID) {
							return { ...item, checked: checked };
						}
						return item;
					}),
				},
			};
		})
	};

	/**
	 * @description Function to handle download and file names for passwords/keys
	 * @param type String (password/key)
	 * @param downloadRequest String all/selective/single
	 * @param key String password/key name
	 * @memberof DeviceDetailsContainer
	 */
	handleDownload = (type, downloadRequest, key) => {
		const { deviceDetails } = this.state;
		const deviceId = deviceDetails?.details?.serialNo;
		let fileName = '';

		switch (type) {
			case 'password':
				let passwordTypes = '';
				let resetPasswordCheckbox = false;
				switch (downloadRequest) {
					case 'downloadAll':
						fileName = deviceId + '_Passwords_All.json';
						// passwordTypes = deviceDetails.passwords.map(p => p.key).join(',');
						passwordTypes = 'all';
						break;
					case 'downloadSelected':
						fileName = deviceId + '_Passwords.json';
						resetPasswordCheckbox = true;
						passwordTypes = deviceDetails.passwords.filter(p => p.checked).map(p => p.key).join(',');
						break;
					default:
						fileName = deviceId + '_' + key + '.json';
						passwordTypes = key;
						break;
				}
				this.handleDownloadDevicePasswords(passwordTypes, fileName, resetPasswordCheckbox);
				break;

			case 'key':
				let keyTypes = '';
				let resetKeyCheckbox = false;
				switch (downloadRequest) {
					case 'downloadAll':
						fileName = deviceId + '_Keys_All.zip';
						// keyTypes = deviceDetails.keys.map(p => p.key).join(',');
						keyTypes = 'all';
						break;
					case 'downloadSelected':
						fileName = deviceId + '_Keys.zip';
						resetKeyCheckbox = true;
						keyTypes = deviceDetails.keys.filter(p => p.checked).map(p => p.key).join(',');
						break;
					default:
						fileName = deviceId + '_' + key + '.zip';
						keyTypes = key;
						break;
				}
				this.handleDownloadDeviceKeys(keyTypes, fileName, resetKeyCheckbox);
				break;

			case 'tpm':
				this.handleDownloadDeviceTPMCertificate(deviceId + '_TPMCertificate.pem');
				break;

			default:
				break;
		}
	}

	/**
	 * @description Function to handle API call for download
	 * @param passwordTypes Array of passwords to be downloaded
	 * @param filename String custom filename
	 * @param resetCheckbox Boolean to reset the checkbox array
	 * @memberof DeviceDetailsContainer
	 */
	handleDownloadDevicePasswords = (passwordTypes, filename, resetCheckbox) => {
		if (this.state.isDownloading) {
			this.setState({ passwordDownloadError: 'Downloading encrypted file...' });
			return
		}
		this.setState({ isDownloading: true, passwordDownloadError: '' });
		const payload = {
			serialNo: this.state.deviceSerialNo,
			passwordTypes: passwordTypes,
		};
		downloadDevicePasswords(payload, (err, resp) => {
			if (err) {
				this.setState({ isDownloading: false, passwordDownloadError: `Download Error: ${err.message}` });
			} else {
				this.downloadAsFile('password', resp.data, filename, 'application/octet-stream');
				this.setState({ isDownloading: false, passwordDownloadError: '' });
				if (resetCheckbox) {
					this.setState(prevState => {
						return {
							deviceDetails: {
								...prevState.deviceDetails,
								passwords: prevState.deviceDetails.passwords.map((item) => {
									return { ...item, checked: false };
								}),
							},
						};
					})
				}
			}
		});
	};

	/**
	 * @description Function to handle API call for download
	 * @param keyTypes Array of keys to be downloaded
	 * @param filename String custom filename
	 * @param resetCheckbox Boolean to reset the checkbox array
	 * @memberof DeviceDetailsContainer
	 */
	handleDownloadDeviceKeys = (keyTypes, filename, resetCheckbox) => {
		if (this.state.isDownloading) {
			this.setState({ keyDownloadError: 'Downloading encrypted file...' });
			return
		}
		this.setState({ isDownloading: true, keyDownloadError: '' });
		const payload = {
			serialNo: this.state.deviceSerialNo,
			type: keyTypes.toString().toLowerCase(),
		};
		downloadDeviceKeys(payload, (err, resp) => {
			if (err) {
				this.setState({ isDownloading: false, keyDownloadError: `Download Error: ${err.message}` });
			} else {
				this.downloadAsFile('keys', resp.data, filename, 'application/zip');
				this.setState({ isDownloading: false, keyDownloadError: '' });
				if (resetCheckbox) {
					this.setState(prevState => {
						return {
							deviceDetails: {
								...prevState.deviceDetails,
								keys: prevState.deviceDetails.keys.map((item) => {
									return { ...item, checked: false };
								}),
							},
						};
					})
				}
			}
		});
	};

	/**
 * @description Function to handle API call for download TPM Certificate
 * @param filename String custom filename
 * @memberof DeviceDetailsContainer
 */
	handleDownloadDeviceTPMCertificate = (filename) => {
		if (this.state.isDownloading) {
			this.setState({ tpmCertificateDownloadError: 'Downloading encrypted file...' });
			return
		}
		this.setState({ isDownloading: true, tpmCertificateDownloadError: '' });
		downloadDeviceTPMCertificate(this.state.deviceSerialNo, (err, resp) => {
			if (err) {
				this.setState({ isDownloading: false, tpmCertificateDownloadError: `Download Error: ${err.message}` });
			} else {
				this.downloadAsFile('tpm_certificate', resp.data, filename, 'application/octet-stream');
				this.setState({ isDownloading: false, tpmCertificateDownloadError: '' });

			}
		});
	};


	/**
	 * @description Function to handle download as JSON
	 * @param content String to be downloaded
	 * @param filename String custom filename
	 * @memberof DeviceDetailsContainer
	 */
	downloadAsFile = (downloadType, content, filename, type) => {
		const a = document.createElement("a");
		a.style = "display:none";
		const file = (downloadType === 'password') ? content : new Blob([content], { type: type });
		a.href = URL.createObjectURL(file);
		a.download = filename;
		a.click();
		URL.revokeObjectURL(a.href);
		a.remove();
	};

	render() {
		const { fetchTPMCertificateError, tpmCertificateDownloadError, deviceTPMCertificate, enableTPMCertificate, loading, deviceDetails, fetchError, passwordDownloadError, keyDownloadError, disbalePasswordDownloadBtn, disbaleKeyDownloadBtn } = this.state;
		return (
			<DeviceDetailsComponent
				showByDefault={false}
				handleCheckSelection={this.handleCheckSelection}
				handleDownload={this.handleDownload}
				loading={loading}
				deviceDetails={deviceDetails}
				fetchError={fetchError}
				passwordDownloadError={passwordDownloadError}
				keyDownloadError={keyDownloadError}
				disbalePasswordDownloadBtn={disbalePasswordDownloadBtn}
				disbaleKeyDownloadBtn={disbaleKeyDownloadBtn}
				enableTPMCertificate={enableTPMCertificate}
				deviceTPMCertificate={deviceTPMCertificate}
				tpmCertificateDownloadError={tpmCertificateDownloadError}
				fetchTPMCertificateError={fetchTPMCertificateError}
			/>
		);
	}
}
